import { Color, Vector2, Vector3 } from 'three';
import type { Camera, Object3D, Scene } from 'three';
import { Sound } from '../audio/sound';
import type { EffectCard } from '../cards/cards';
import { t } from '../i18n/i18n';
import type { Bout } from '../rules/bout';
import type { PunchReport, PunchStats, Side } from '../rules/types';
import { flashBurst, stirCrowd } from '../view/arena/time';
import { boxerFistInGlove } from '../view/bodies';
import type { BoxerAnimator } from '../view/boxer/animator';
import type { BoxerRig } from '../view/boxer/boxer';
import type { CameraDirector } from '../view/camera/director';
import { Particles } from '../view/effects/particles';
import { Shake } from '../view/effects/shake';
import { ImpactFlash } from '../view/effects/impactFlash';
import { DizzyStars } from '../view/effects/stars';
import { TimeWarp } from '../view/effects/timeWarp';
import { Trail } from '../view/effects/trails';
import type { Hud } from '../view/hud/hud';
import type { FirstPerson } from '../view/player/firstPerson';
import type { Pipeline } from '../view/render/pipeline';

export interface Stage {
  readonly scene: Scene;
  readonly camera: Camera;
  readonly pipeline: Pipeline;
  readonly hud: Hud;
  readonly boxer: BoxerRig;
  readonly animator: BoxerAnimator;
  readonly firstPerson: FirstPerson;
  readonly director: CameraDirector;
  readonly effects: EffectCard;
}

/** Where on him the player's punches land, in the world, updated each frame by the game. */
export interface Targets {
  readonly head: Vector3;
  readonly body: Vector3;
  readonly skull: Object3D;
}

const at = new Vector3();
/** The slow orbit round a knockout, in real seconds: it ends as the result comes up. */
export const knockoutShotSeconds = 3.4;
const away = new Vector3();
const eyes = new Vector3();
const buffer = new Vector2();

/**
 * How the bout looks and sounds when things happen in it: the hit-stop and the shake of a punch, sparks and sweat off a
 * clean hit, a puff off a block, stars round a dazed head, red at the edges when the player is hurt, the knockout in
 * slow motion, the bell, the thud of gloves, the crowd. It only reacts to the rules' events; it never changes them.
 */
export class FightShow {
  readonly time = new TimeWarp();
  readonly sound = new Sound();
  private readonly shake = new Shake();
  private readonly particles = new Particles();
  private readonly stars = new DizzyStars();
  private readonly flash = new ImpactFlash();
  private hurt = 0;
  private excitement = 0;
  /** Real seconds since the bout began, for telling a combo from two separate hits. */
  private clock = 0;
  private combo = 0;
  private lastClean = -10;
  private lastDefence = -10;
  private readonly trails: Record<'mine' | 'his', Record<'left' | 'right', Trail>>;
  private wasDodging = false;
  private effects: EffectCard;

  constructor(private readonly stage: Stage, private readonly targets: Targets) {
    this.effects = stage.effects;
    stage.scene.add(this.particles.points, this.stars.group, this.flash.sprite);
    const trail = (r: number, g: number, b: number) => new Trail(new Color(r, g, b), 0.07);
    this.trails = {
      mine: { left: trail(0.55, 0.75, 1), right: trail(0.55, 0.75, 1) },
      his: { left: trail(1, 0.72, 0.55), right: trail(1, 0.72, 0.55) },
    };
    for (const side of Object.values(this.trails)) for (const one of Object.values(side)) stage.scene.add(one.mesh);
    stage.animator.floored.add((hips) => this.floored(hips));
  }

  /** A new bout: react to what happens in it. */
  watch(bout: Bout): void {
    this.hurt = 0;
    this.excitement = 0;
    this.combo = 0;
    this.lastClean = this.lastDefence = -10;
    bout.punchStarted.add(({ side, punch }) => this.started(side, punch));
    bout.punchResolved.add((report) => (report.attacker === 'player' ? this.landed(report) : this.taken(report)));
    bout.punchInterrupted.add(({ side }) => {
      if (side !== 'opponent') return;
      this.stage.hud.interrupted();
      this.rouse(0.3);
      flashBurst.value = Math.max(flashBurst.value, 0.3);
    });
    bout.roundStarted.add(() => this.fight());
    bout.roundEnded.add(() => {
      this.sound.play('bell', { volume: 0.8 });
      this.sound.play('bell', { volume: 0.8, delay: 0.35 });
    });
    bout.knockedOut.add((side) => this.knockout(side));
    bout.over.add((result) => {
      if (!result.byKnockout) this.stage.hud.announce(t('time-up'));
      if (result.winner === 'opponent') this.stage.animator.celebrate();
      if (result.winner === 'player') this.rouse(1);
    });
  }

  /** Sashko changed the effects card. */
  setEffects(effects: EffectCard): void {
    this.effects = effects;
  }

  /** The bell and FIGHT! as a round begins. */
  fight(): void {
    this.stage.hud.announce(t('fight-banner'), 'is-fight');
    this.sound.play('bell');
    this.rouse(0.4);
  }

  /** The player ducked: the air of the punch going past. */
  dodged(): void {
    this.sound.play('whoosh', { volume: 0.7 });
  }

  /** Every frame: real seconds for the view and the sound, the game's seconds for what moves in the ring. */
  update(real: number, game: number, bout: Bout): void {
    const stage = this.stage;
    stage.firstPerson.setShake(this.shake.update(real));
    this.hurt = Math.max(0, this.hurt - real * 1.8);
    const health = bout.player.health.fraction;
    const weak = health < 0.3 ? ((0.3 - health) / 0.3) * 0.55 : 0;
    const level = Math.max(this.hurt, weak);
    stage.pipeline.setHurt(level);
    stage.hud.setHurt(level);
    stage.pipeline.renderer.getDrawingBufferSize(buffer);
    this.particles.setViewHeight(buffer.y);
    this.particles.update(game);
    this.flash.update(game);
    const him = bout.opponent;
    this.stars.update(game, this.targets.skull.getWorldPosition(at), him.state === 'staggered' || him.state === 'knockedOut');
    this.excitement = Math.max(0, this.excitement - real * 0.22);
    stirCrowd(this.excitement, real);
    this.sound.crowd(this.excitement);
    flashBurst.value = Math.max(0, flashBurst.value - real * 0.45);
    this.clock += real;
    this.wasDodging = bout.player.state === 'dodging';
    stage.camera.getWorldPosition(eyes);
    for (const hand of ['left', 'right'] as const) {
      // The player's gloves ride on the camera: while the director moves it, they are hidden and would only streak
      // after it.
      if (stage.director.filming) this.trails.mine[hand].putOut();
      else this.trails.mine[hand].update(game, stage.firstPerson.fist(hand, at), eyes);
      this.trails.his[hand].update(game, stage.boxer[hand].glove.localToWorld(at.copy(boxerFistInGlove)), eyes);
    }
  }

  private started(side: Side, punch: PunchStats): void {
    const heavy = punch.staggers || punch.action === 'super';
    this.sound.play('whoosh', { volume: side === 'player' ? 0.16 : 0.24, rate: heavy ? 0.8 : 1.15 });
  }

  // The player's punch on him: sparks and sweat off his face or body, a short hit-stop, the crowd.
  private landed(report: PunchReport): void {
    const { animator } = this.stage;
    const effects = this.effects;
    const punch = report.punch;
    const isSuper = punch.action === 'super';
    const spot = punch.target === 'body' ? this.targets.body : this.targets.head;
    // The spray bursts back off his face towards the player and out along the punch, where the player can see it.
    this.stage.camera.getWorldPosition(eyes);
    away.subVectors(eyes, spot).setY(0).normalize().multiplyScalar(0.7);
    away.y += 0.35;
    if (punch.action === 'hook' || isSuper) away.x += punch.hand === 'left' ? 0.9 : -0.9;
    if (punch.action === 'uppercut') away.y += 0.9;
    if (report.outcome === 'blocked') {
      animator.blocked();
      this.particles.burst('puff', spot, away, 8, 1.2);
      this.sound.play('block', { volume: 0.8 });
      this.shake.add(effects.shakeBlocked * 0.5);
      return;
    }
    if (report.outcome !== 'clean') return;
    animator.hit(punch, punch.staggers ? 1.5 : 1);
    // Hits one after another make a combo; a hit straight after blocking or slipping his is a counter.
    this.combo = this.clock - this.lastClean < 1.4 ? this.combo + 1 : 1;
    this.lastClean = this.clock;
    const countered = this.clock - this.lastDefence < 1;
    if (countered) {
      this.lastDefence = -10;
      this.stage.hud.counter();
    } else if (this.combo >= 2) {
      this.stage.hud.combo(this.combo);
    }
    const big = isSuper || punch.staggers || countered;
    if (big) {
      this.stage.pipeline.shockwave(spot, isSuper ? 1 : 0.6);
      this.stage.hud.rush();
      flashBurst.value = Math.max(flashBurst.value, isSuper ? 0.8 : 0.35);
    }
    if (isSuper || punch.staggers) this.stage.director.punchIn(isSuper ? 1 : 0.55);
    this.flash.pop(spot, isSuper ? 0.9 : punch.staggers ? 0.55 : 0.38);
    this.particles.burst('spark', spot, away, isSuper ? effects.superSparks : effects.sparks);
    this.particles.burst('sweat', spot, away.setY(away.y + 0.4), effects.sweat, 1);
    this.time.freeze(isSuper ? effects.hitStopSuperSeconds : punch.staggers ? effects.hitStopStaggerSeconds : effects.hitStopSeconds);
    this.shake.add(punch.staggers ? effects.shakeLandedStagger : effects.shakeLanded);
    this.sound.play('punch', { rate: punch.staggers ? 0.85 : 1.05 });
    if (isSuper) this.sound.play('roar', { volume: 0.8 });
    else if (punch.staggers) this.sound.play('cheer', { volume: 0.5 });
    this.rouse(isSuper ? 0.8 : punch.staggers ? 0.35 : 0.12);
  }

  // His punch on the player: the view jolts and shakes, red closes in; a block puffs where his glove stopped.
  private taken(report: PunchReport): void {
    const { firstPerson } = this.stage;
    const effects = this.effects;
    const punch = report.punch;
    if (report.outcome === 'dodged') {
      this.lastDefence = this.clock;
      this.dodged();
      // A moment of slow motion as it goes past: the player should feel he slipped it.
      this.time.slow(0.35, 0.3);
      this.stage.hud.slipped();
      return;
    }
    // The hit has already knocked him out of his dodge by now: was he ducking a frame ago?
    if (report.outcome === 'clean' && punch.target === 'body' && this.wasDodging) this.stage.hud.bodyNotDodged();
    firstPerson.hit(punch, report.outcome === 'blocked');
    if (report.outcome === 'blocked') {
      this.lastDefence = this.clock;
      this.stage.boxer[punch.hand].glove.getWorldPosition(at);
      this.stage.camera.getWorldPosition(eyes);
      this.particles.burst('puff', at.lerp(eyes, 0.35), away.subVectors(eyes, at).normalize(), 10, 1.4);
      this.sound.play('block');
      this.shake.add(effects.shakeBlocked);
      return;
    }
    this.hurt = 1;
    this.combo = 0;
    if (punch.staggers) {
      this.stage.director.punchIn(-0.35);
      this.stage.pipeline.shockwave(this.stage.boxer[punch.hand].glove.getWorldPosition(at), 0.5);
    }
    this.stage.boxer[punch.hand].glove.getWorldPosition(at);
    this.flash.pop(at.lerp(this.stage.camera.getWorldPosition(eyes), 0.3), punch.staggers ? 0.5 : 0.36);
    this.time.freeze(effects.hitStopTakenSeconds);
    this.shake.add(punch.staggers ? effects.shakeTakenStagger : effects.shakeTaken);
    this.sound.play('punch', { rate: 0.8, volume: 1 });
    this.rouse(0.15);
  }

  private knockout(side: Side): void {
    const { hud, animator, firstPerson } = this.stage;
    const effects = this.effects;
    hud.announce(t('knockout'), 'is-ko');
    hud.rush();
    flashBurst.value = 1;
    this.time.slow(effects.knockoutSlowScale, effects.knockoutSlowSeconds);
    this.shake.add(1);
    this.sound.play('roar');
    this.sound.play('bell', { delay: 0.6 });
    this.rouse(1);
    if (side === 'opponent') {
      animator.knockedOut();
      firstPerson.watchFall();
      this.stage.director.knockout(this.stage.boxer.head, this.stage.boxer.root.position, knockoutShotSeconds);
    } else {
      firstPerson.knockedOut();
    }
  }

  // The knocked-out man hits the canvas: a puff of dust off it, a thud, a jolt through the ring.
  private floored(hips: Vector3): void {
    at.set(hips.x, 0.08, hips.z);
    this.particles.burst('puff', at, away.set(0, 1, 0), 26, 2.2);
    this.shake.add(0.6);
    this.sound.play('punch', { rate: 0.55, volume: 1 });
    this.sound.play('block', { rate: 0.6, volume: 0.8 });
    this.stage.pipeline.shockwave(at, 0.45);
  }

  private rouse(amount: number): void {
    this.excitement = Math.min(1, this.excitement + amount);
  }
}
